// @ts-ignore Cookie TS definition is in 1.0.0
// @see https://github.com/jshttp/cookie/releases/tag/v1.0.0
import cookie from 'cookie';
import { Exposure, ExposureTrackingProvider, Variant } from '@amplitude/experiment-js-client';
import { v4 as uuidv4 } from 'uuid';
import * as Amplitude from '@amplitude/analytics-browser';

import { setCookie } from './cookies';

export { Experiment } from '@amplitude/experiment-js-client';
export type { ExposureTrackingProvider, Exposure } from '@amplitude/experiment-js-client';

// This closure solves a race condition between setCookie in getDeviceIdFromAmplitudeCookie
// and the call to initializeTracking which tries to read the cookie.
// The closure stores the newly created deviceId for access when the cookie is not set in time
// This is especially important as if the race-condition fails, it will use a different deviceId
// for the first page load, than the one used for the subsequent page loads.
// ------------------------------------------------------------
// Typical use case: I have no cookie at all, I load a web page which creates the deviceId
// and uses it right away to initialize the Experiment SDK.
let liveDeviceId: string | undefined;

export const createGtmExposureTracker = (dataLayer: any): ExposureTrackingProvider => ({
  track: (exposure: Exposure) => {
    dataLayer.push({ event: 'LogEvent', eventType: '$exposure', eventProperties: exposure });
  },
});

export const createExposureTracker = (): ExposureTrackingProvider => ({
  track: (exposure: Exposure) => {
    Amplitude.track('$exposure', exposure);
  },
});

// Get Amplitude Analytics deviceId, if set from rails backend or by the frontend amplitude tracking SDK.
// This is very important in a web context, to make Analytics events and Experiment assignments
// stick together into a single user journey
export const getDeviceIdFromAmplitudeCookie = (apiKey: string) => {
  const cookies = cookie.parse(document.cookie);
  const cookieName = `AMP_${apiKey.substring(0, 10)}`;
  const amplitudeCookie = cookies[cookieName];

  if (liveDeviceId) return liveDeviceId;

  if (!amplitudeCookie) {
    const storedUuid = uuidv4();
    liveDeviceId = storedUuid;
    setCookie({
      name: cookieName,
      value: btoa(JSON.stringify({ deviceId: storedUuid })),
    });
    return storedUuid;
  }

  try {
    return JSON.parse(decodeURIComponent(atob(amplitudeCookie))).deviceId;
  } catch (error) {
    return undefined;
  }
};

export const resetLiveDeviceId = () => {
  liveDeviceId = undefined;
};

export const trackExposure = (flagKey: string, variant: Variant) => {
  Amplitude.track('$exposure', {
    flag_key: flagKey,
    variant: variant.value,
    metadata: variant.metadata,
    experiment_key: variant.expKey,
  });
};
